/**
 * Public campaign landing — stable URL: /campaign/:slug
 * Loads copy + product from API; primary CTA goes to checkout deep link.
 *
 * API returns 200 whenever the slug exists so Tammy can preview paused / "product off menu"
 * states with a clear message instead of a blank 404.
 *
 * Features:
 *  - Progress bar: shows sold / max visually in the product card.
 *  - Closing-soon / almost-sold-out badges in the copy card.
 *  - Live countdown (updates every minute) when ends_at is set.
 *  - Share button with copy-to-clipboard.
 *  - Organizer line (name + contact) when set by Tammy.
 */

import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { applySeo } from '../utils/seo';
import { publicMessageFromApiErrorResponse } from '../lib/publicSiteErrorMessage';

const API_URL = import.meta.env.VITE_API_URL || '';

interface PublicCampaign {
  id: string;
  slug: string;
  title: string;
  /** When false, hide the visible "Fundraiser" line + large title (poster image may carry all wording). */
  show_landing_heading?: boolean | null;
  hero_text: string | null;
  thank_you_text: string | null;
  hero_image_url?: string | null;
  /** ISO timestamp — shown as countdown; also used for "closing soon" badge. */
  ends_at?: string | null;
  /** Name of the group running the fundraiser, e.g. "Lincoln School PTO". */
  organizer_name?: string | null;
  /** Free-form contact line, e.g. "Jane | 705-555-1234". */
  organizer_contact?: string | null;
  max_units: number | null;
  sold_units: number;
  sold_out: boolean;
  menu_item_eligible: boolean;
  menu_item_block_reason: string | null;
}

interface PublicMenuItem {
  id: string;
  name: string;
  price: number;
  category: string;
  image_url: string | null;
  is_available?: boolean;
  is_orderable_online?: boolean | null;
}

interface PublicCampaignApiResponse {
  campaign: PublicCampaign;
  menu_item: PublicMenuItem | null;
  /** When false, show message and link to menu only (paused, ended, product hidden, etc.). */
  checkout_allowed?: boolean;
  status?: string;
}

/** Compute how many ms remain until an ISO date string. Returns null if already past. */
function msUntil(isoString: string): number | null {
  const ms = new Date(isoString).getTime() - Date.now();
  return ms > 0 ? ms : null;
}

export default function CampaignPage() {
  const { slug } = useParams<{ slug: string }>();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [campaign, setCampaign] = useState<PublicCampaign | null>(null);
  const [menuItem, setMenuItem] = useState<PublicMenuItem | null>(null);
  const [checkoutAllowed, setCheckoutAllowed] = useState(false);

  // Share button: copy URL to clipboard, show "Copied!" feedback for 2.5 s
  const [copied, setCopied] = useState(false);

  // Countdown: { days, hours, minutes } — recalculated every minute
  const [timeLeft, setTimeLeft] = useState<{ days: number; hours: number; minutes: number } | null>(null);

  useEffect(() => {
    if (!slug) return;
    setLoading(true);
    setError(null);
    const url = API_URL
      ? `${API_URL}/api/campaigns/public/${encodeURIComponent(slug)}`
      : `/api/campaigns/public/${encodeURIComponent(slug)}`;
    fetch(url)
      .then(async (res) => {
        if (!res.ok) {
          if (res.status === 404) {
            setError('We could not find a fundraiser with this link. Check the spelling or ask the organizer for the latest link.');
            return null;
          }
          let body: unknown = null;
          try { body = await res.json(); } catch { body = null; }
          throw new Error(publicMessageFromApiErrorResponse(res.status, body));
        }
        return res.json() as Promise<PublicCampaignApiResponse>;
      })
      .then((data: PublicCampaignApiResponse | null) => {
        if (!data?.campaign) return;
        setCampaign(data.campaign);
        setMenuItem(data.menu_item ?? null);
        const allow =
          data.checkout_allowed !== undefined
            ? data.checkout_allowed
            : !data.campaign.sold_out && data.campaign.menu_item_eligible;
        setCheckoutAllowed(!!allow);
        const heroTrim = data.campaign.hero_text?.trim();
        const hideHeadline = data.campaign.show_landing_heading === false;
        applySeo({
          title: `${data.campaign.title} | Nanny B's Bakery`,
          description:
            heroTrim ||
            (hideHeadline
              ? `Order ${data.menu_item?.name || 'fundraiser treats'} for pickup at Nanny B's Bakery in Barrie, Ontario.`
              : `Support a fundraiser — ${data.menu_item?.name || "Nanny B's Bakery"} treats for pickup in Barrie, Ontario.`),
          canonicalPath: `/campaign/${slug}`,
        });
      })
      .catch((e) => setError(e instanceof Error ? e.message : 'Failed to load'))
      .finally(() => setLoading(false));
  }, [slug]);

  // Countdown: tick every 60 s, or clear when past ends_at
  useEffect(() => {
    const endsAt = campaign?.ends_at;
    if (!endsAt) { setTimeLeft(null); return; }

    const calc = () => {
      const ms = msUntil(endsAt);
      if (!ms) { setTimeLeft(null); return; }
      setTimeLeft({
        days: Math.floor(ms / 86_400_000),
        hours: Math.floor((ms % 86_400_000) / 3_600_000),
        minutes: Math.floor((ms % 3_600_000) / 60_000),
      });
    };

    calc();
    const id = setInterval(calc, 60_000);
    return () => clearInterval(id);
  }, [campaign?.ends_at]);

  const checkoutHref = slug ? `/order?campaign=${encodeURIComponent(slug)}` : '/order';

  const handleCopy = () => {
    navigator.clipboard.writeText(window.location.href).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2500);
    });
  };

  if (loading) {
    return (
      <div className="container-custom py-16 max-w-3xl mx-auto animate-pulse">
        <div className="h-8 bg-gray-200 rounded w-1/3 mb-4" />
        <div className="h-12 bg-gray-200 rounded w-2/3 mb-8" />
        <div className="h-40 bg-gray-100 rounded-2xl mb-8" />
        <div className="h-56 bg-gray-100 rounded-2xl" />
      </div>
    );
  }

  if (error || !campaign) {
    return (
      <div className="container-custom py-16 text-center max-w-lg mx-auto">
        <div className="rounded-2xl border border-gray-200 bg-white shadow-sm p-8">
          <p className="text-gray-800 mb-6">{error || 'Not found.'}</p>
          <Link to="/menu" className="btn-primary inline-block">Browse our menu</Link>
        </div>
      </div>
    );
  }

  // ── derived values ────────────────────────────────────────────────────────
  const blockReason      = campaign.menu_item_block_reason;
  const canBuy           = checkoutAllowed;
  const showStatusBanner = !canBuy && blockReason;

  // Treat missing / null as true so older API rows behave like before the column existed.
  const showTitleBlock = campaign.show_landing_heading !== false;

  // Progress bar — only meaningful when a cap (max_units) is set
  const hasProgress = campaign.max_units != null && campaign.max_units > 0;
  const progressPct = hasProgress
    ? Math.min(100, Math.round((campaign.sold_units / campaign.max_units!) * 100))
    : 0;

  // Badges: "Closing soon" — ends within 3 days; "Almost sold out" — >= 80 % of cap
  const closingSoon   = !!campaign.ends_at && !!msUntil(campaign.ends_at) && msUntil(campaign.ends_at)! < 3 * 86_400_000;
  const almostSoldOut = hasProgress && progressPct >= 80 && !campaign.sold_out;
  const showBadges    = (closingSoon || almostSoldOut) && !campaign.sold_out;

  // Countdown label: e.g. "3 days 5 hours" or "2 hours 10 min"
  const countdownLabel = timeLeft
    ? timeLeft.days > 0
      ? `${timeLeft.days} day${timeLeft.days !== 1 ? 's' : ''} ${timeLeft.hours} hr`
      : `${timeLeft.hours} hr ${timeLeft.minutes} min`
    : null;

  // Show the copy card when there's any text content or a badge/countdown to display.
  // Omit entirely only for true poster-only layouts (no text, no heading, no badges).
  const hasCopyCard =
    showTitleBlock ||
    !!campaign.hero_text?.trim() ||
    !!campaign.thank_you_text?.trim() ||
    !!showStatusBanner ||
    showBadges ||
    !!countdownLabel;

  // Banner alt — when heading is hidden, the image is the primary title element
  const bannerAlt = showTitleBlock ? '' : campaign.title;

  const titleHeading = showTitleBlock ? (
    <>
      <p className="text-xs font-semibold uppercase tracking-widest text-primary-700 mb-2">Fundraiser</p>
      <h1 className="text-3xl sm:text-4xl md:text-5xl font-display font-bold text-gray-900 mb-4 leading-tight">
        {campaign.title}
      </h1>
    </>
  ) : (
    <h1 className="sr-only">{campaign.title}</h1>
  );

  return (
    <div className="container-custom py-8 md:py-12">
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.35 }}
        className="max-w-3xl mx-auto"
      >
        {/* Optional wide banner — https URLs only (CSP may still block unknown hosts until relaxed). */}
        {campaign.hero_image_url && /^https:\/\//i.test(campaign.hero_image_url) ? (
          <div className="rounded-2xl overflow-hidden border border-gray-200/80 shadow-md mb-8 ring-1 ring-black/5">
            <img
              src={campaign.hero_image_url}
              alt={bannerAlt}
              className="w-full max-h-52 sm:max-h-64 object-cover bg-gray-100"
            />
          </div>
        ) : null}

        {/* Copy card: omit entirely when Tammy uses a poster-only layout (no text content and headline hidden). */}
        {hasCopyCard ? (
          <div className="relative rounded-3xl bg-gradient-to-br from-white via-violet-50/40 to-amber-50/30 border border-violet-100/80 shadow-xl shadow-violet-900/5 px-6 py-8 sm:px-10 sm:py-10 overflow-hidden">
            <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top_right,_var(--tw-gradient-stops))] from-violet-100/50 via-transparent to-transparent pointer-events-none" />
            <div className="relative">
              {/* Status badges — "Closing soon" / "Almost sold out" */}
              {showBadges && (
                <div className="flex flex-wrap gap-2 mb-4">
                  {closingSoon && (
                    <span className="inline-flex items-center gap-1.5 rounded-full bg-amber-100 border border-amber-200 px-3 py-1 text-xs font-semibold text-amber-800">
                      Closing soon
                    </span>
                  )}
                  {almostSoldOut && (
                    <span className="inline-flex items-center gap-1.5 rounded-full bg-rose-100 border border-rose-200 px-3 py-1 text-xs font-semibold text-rose-800">
                      Almost sold out
                    </span>
                  )}
                </div>
              )}

              {titleHeading}

              {campaign.hero_text ? (
                <p className="text-lg text-gray-700 whitespace-pre-line mb-6 leading-relaxed">
                  {campaign.hero_text}
                </p>
              ) : null}

              {/* Countdown pill — only shows when ends_at is in the future */}
              {countdownLabel && (
                <p className="inline-flex items-center gap-1.5 rounded-full bg-white/80 border border-violet-100 px-3 py-1 text-xs font-medium text-gray-600 mb-6 shadow-sm">
                  Closes in{' '}
                  <span className="font-semibold text-gray-800">{countdownLabel}</span>
                </p>
              )}

              {showStatusBanner ? (
                <div
                  role="status"
                  className="rounded-xl border border-amber-200 bg-amber-50/95 px-4 py-3 text-amber-950 text-sm mb-8 leading-relaxed"
                >
                  {blockReason}
                </div>
              ) : null}

              {campaign.thank_you_text ? (
                <div className="rounded-xl border border-white/80 bg-white/70 backdrop-blur px-5 py-4 mb-8 text-gray-800 whitespace-pre-line text-sm sm:text-base leading-relaxed shadow-inner">
                  {campaign.thank_you_text}
                </div>
              ) : null}
            </div>
          </div>
        ) : (
          titleHeading
        )}

        {/* Product card */}
        {menuItem ? (
          <div className={`bg-white rounded-2xl shadow-lg border border-gray-100 overflow-hidden ${hasCopyCard ? 'mt-10' : 'mt-8'}`}>
            <div className="md:flex">
              {menuItem.image_url ? (
                <div className="md:w-2/5 bg-gradient-to-br from-gray-50 to-gray-100">
                  <img src={menuItem.image_url} alt="" className="w-full h-56 md:min-h-[280px] md:h-full object-cover" />
                </div>
              ) : (
                <div className="md:w-2/5 bg-gradient-to-br from-gray-50 to-gray-100 flex items-center justify-center h-48 md:min-h-[280px] text-gray-400 text-sm px-4 text-center">
                  Photo coming soon — see this treat on our full menu.
                </div>
              )}
              <div className="p-6 md:w-3/5 flex flex-col justify-center">
                <h2 className="text-2xl font-display font-bold text-gray-900 mb-1">{menuItem.name}</h2>
                <p className="text-sm text-gray-500 mb-3">{menuItem.category}</p>
                <p className="text-3xl font-bold text-primary-600 mb-4">${Number(menuItem.price).toFixed(2)}</p>

                {/* Progress bar — shows sold / max spots visually */}
                {hasProgress && (
                  <div className="mb-4">
                    <div className="flex justify-between text-xs text-gray-500 mb-1.5">
                      <span>
                        <span className="font-semibold text-gray-800">{campaign.sold_units}</span> reserved
                      </span>
                      <span>
                        {campaign.max_units} spots total
                      </span>
                    </div>
                    <div className="h-2 rounded-full bg-gray-100 overflow-hidden">
                      <div
                        className={`h-full rounded-full transition-all ${progressPct >= 80 ? 'bg-rose-400' : 'bg-primary-500'}`}
                        style={{ width: `${progressPct}%` }}
                      />
                    </div>
                    {campaign.sold_out && (
                      <p className="text-xs font-semibold text-rose-600 mt-1">Fully reserved</p>
                    )}
                  </div>
                )}

                {/* Sold / max text when no cap is set */}
                {!hasProgress && campaign.sold_units > 0 && (
                  <p className="text-sm text-gray-600 mb-4">
                    <span className="font-medium text-gray-800">{campaign.sold_units}</span> reserved so far
                  </p>
                )}

                {canBuy ? (
                  <Link
                    to={checkoutHref}
                    className="btn-primary text-center text-lg py-3.5 shadow-md hover:shadow-lg transition-shadow"
                  >
                    Order for pickup — secure checkout
                  </Link>
                ) : (
                  <Link to="/menu" className="btn-secondary text-center text-lg py-3.5">
                    Browse full menu
                  </Link>
                )}
                <p className="text-xs text-gray-500 mt-4 leading-relaxed">
                  {canBuy
                    ? 'You can add more treats on the next step. Same secure checkout as our regular website.'
                    : 'You can still order other items from our menu anytime.'}
                </p>
              </div>
            </div>
          </div>
        ) : (
          <div className={`rounded-2xl border border-gray-200 bg-gray-50/80 p-8 text-center text-gray-700 text-sm ${hasCopyCard ? 'mt-10' : 'mt-8'}`}>
            Product details are not available for this link. Please visit our menu.
          </div>
        )}

        {/* Organizer info — shown when Tammy has filled in organizer_name or organizer_contact */}
        {(campaign.organizer_name || campaign.organizer_contact) && (
          <div className="mt-6 rounded-xl border border-gray-100 bg-gray-50/80 px-5 py-3 text-sm text-gray-600 text-center">
            {campaign.organizer_name && (
              <span className="font-medium text-gray-800">{campaign.organizer_name}</span>
            )}
            {campaign.organizer_name && campaign.organizer_contact && (
              <span className="mx-2 text-gray-300">·</span>
            )}
            {campaign.organizer_contact && <span>{campaign.organizer_contact}</span>}
          </div>
        )}

        {/* Share section — copy-to-clipboard link for the fundraiser page */}
        <div className="mt-6 rounded-xl border border-gray-100 bg-gray-50/60 px-4 py-3 flex items-center gap-3">
          <p className="flex-1 text-xs text-gray-500 truncate select-all">{typeof window !== 'undefined' ? window.location.href : ''}</p>
          <button
            type="button"
            onClick={handleCopy}
            className="shrink-0 rounded-lg border border-gray-200 bg-white px-3 py-1.5 text-xs font-medium text-gray-700 hover:border-primary-300 hover:text-primary-700 transition-colors shadow-sm"
          >
            {copied ? 'Copied!' : 'Copy link'}
          </button>
        </div>

        {/* Footer links */}
        <div className="flex flex-wrap gap-6 justify-center text-sm mt-8 pt-6 border-t border-gray-200/80">
          <Link to="/menu" className="text-primary-700 font-medium hover:underline">Full menu</Link>
          <Link to="/contact" className="text-gray-600 hover:text-primary-700">Contact</Link>
          <Link to="/about" className="text-gray-600 hover:text-primary-700">About us</Link>
        </div>
      </motion.div>
    </div>
  );
}
